import { finalize } from "rxjs";
import { DataService } from "../services/api/data/data.service";
import { LoadingService } from "../services/loading/loading";
import { ModalService } from "../services/modal/modal.service";
import { SnackbarService } from "../services/snackbar/snackbar.service";

/**
 * Delete a field with confirmation dialog and feedback
 * @param fieldId - The ID of the field to delete
 * @param fieldName - Optional field name for better confirmation message
 * @param action - Action label ('Delete' for growers, 'Release' for managers)
 * @param services - Required services (dataService, loadingService, modalService, snackbarService)
 * @param onSuccess - Callback to execute on successful deletion
 * @returns Promise that resolves when deletion is complete or cancelled
 */
export async function deleteFieldUtil(
  fieldId: number,
  fieldName: string | undefined,
  action: "Delete" | "Release",
  services: {
    dataService: DataService;
    loadingService: LoadingService;
    modalService: ModalService;
    snackbarService: SnackbarService;
  },
  onSuccess?: () => void,
): Promise<void> {
  const displayName = fieldName || `Field ${fieldId}`;

  const result = await services.modalService.openDialogAsync({
    title: `${action} Claimed Field?`,
    message: `Are you sure you want to ${action.toLowerCase()} ${displayName}? The field will be available for other users to claim again.`,
    actions: [
      {
        label: "Cancel",
        action: () => "cancel",
      },
      {
        label: action,
        action: () => "confirm",
      },
    ],
  });

  if (result === "cancel") {
    return;
  }

  const loadingMessage = action === "Delete" ? "Deleting" : "Releasing";
  const actionPastTense = action === "Delete" ? "deleted" : "released";

  services.loadingService.start({
    token: "field-updating",
    message: loadingMessage,
  });

  services.dataService
    .deleteFields(fieldId)
    .pipe(finalize(() => services.loadingService.stop("field-updating")))
    .subscribe({
      next: () => {
        services.snackbarService.show({
          message: `Field has been ${actionPastTense}!`,
          duration: 3000,
        });
        if (onSuccess) {
          onSuccess();
        }
      },
      error: (error: any) => {
        console.error(`Error deleting/releasing field ${fieldId}:`, error);
      },
    });
}
